import {
  useDateFormat,
  useProperty,
  useSearch,
  useSiteContext,
  useTranslation,
} from '@eti/providers';
import { css, cx, mediaQueries } from '@eti/styles';
import { constants, date, duration as durationUtil } from '@eti/utils';
import { CarrierLogo, Icon } from '@eti/components';
import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import type { BrandedFareInfo, CarrierNew, Maybe } from '@eti/schema-types';
import { ALL, SOME } from '../../../../../../constants/transportationMeansConstants';
import Stops from '../../../../../../widgets/trip-details/components/Stops';
import { Label } from '../../../v1/styles/quickSortButton';
import { StopoverInfoIndicator } from '../../resultTripCards/components/TripIndicator';
import { useBrandedFareName } from '../../../../../../common/custom-hooks/useBrandedFareName';
import { isMatchingLocation } from '../../../../../../widgets/trip-details/utils/originDestinationUtil';
import type { SearchRouteCodeKindType } from '../../model';
import { SearchRouteCodeKind } from '../../model';
import { routes } from '../../../../../../constants/routesAndApisConstants';

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const itineraryInfoStyles = css`
  font-size: 0.875rem;
  line-height: 100%;

  &:not(:last-child) {
    margin-block-end: 16px;
  }
`;
const departureArrivalStyles = css`
  font-weight: 700;
  justify-content: space-between;

  > div {
    position: relative;
  }

  > p {
    font-size: inherit;
    font-weight: inherit;
    margin-block-start: 0;
  }
`;

const departureArrivalCitiesStyles = css`
  color: #737373;
  font-weight: 400;

  span.duration {
    color: ${vars.colors.text};
    font-size: inherit;
    max-width: fit-content;
  }
`;

const itineraryTimeStyles = css`
  font-size: 1rem;
`;

const carrierDateStyles = css`
  color: ${vars.colors.text};
  justify-content: space-between;

  img {
    height: 20px;
    width: 20px;

    &:not(:last-child) {
      margin-inline-end: 8px;
    }
  }
`;

const overnightIndicatorStyles = css`
  background: ${vars.colors.signal.information.tint};
  border-radius: 3px;
  color: ${vars.colors.signal.information.dark};
  padding: 0 2px;
`;

const selfTransferStyles = css`
  justify-content: space-between;
  margin-block-start: 4px;

  > div {
    background: ${vars.colors.signal.caution.tint};
    border-radius: 8px;
    font-weight: 700;
    padding: 4px 8px;

    > span {
      color: ${vars.colors.signal.caution.dark};
      margin-left: 5px;
    }
  }

  p {
    color: ${vars.colors.text};
  }
`;

const badgesWrapper = css`
  flex: 1;
`;

const cabinClassStyles = css`
  color: #737373;
  font-size: 0.875rem;

  @media ${mediaQueries.small.only} {
    line-height: 1.1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 180px;
  }
`;

const dotSeparatorStyles = css`
  background-color: #737373;
  border-radius: 2px;
  height: 3px;
  width: 3px;
`;

const nearbyAirportStyles = css`
  background: ${vars.colors.signal.caution.tint};
  color: ${vars.colors.signal.caution.dark};
  padding: 0 4px;
`;

export type TrainSegmentType = 'ALL' | 'SOME' | 'NONE';
interface ItineraryDetailsLocationTimeProps {
  arrivalCode: string;
  arrivalCityCode?: string;
  arrivalDate: string;
  boundNumber?: number;
  brandedFareInformation?: Maybe<BrandedFareInfo> | undefined;
  cabinClass?: string;
  carriers: CarrierNew[];
  departureCode: string;
  departureCityCode?: string;
  departureDate: string;
  duration: number;
  isSelfTransfer: boolean;
  marketingCarrier?: CarrierNew | undefined;
  numberOfTechnicalStops: number;
  stopoverType?: string | null;
  stops: number;
  trainSegment: TrainSegmentType;
}

const ItineraryDetailsLocationTime = ({
  arrivalCode,
  arrivalCityCode,
  arrivalDate,
  boundNumber,
  brandedFareInformation,
  cabinClass,
  carriers,
  departureCode,
  departureCityCode,
  departureDate,
  duration,
  isSelfTransfer,
  marketingCarrier,
  numberOfTechnicalStops,
  stopoverType,
  stops,
  trainSegment,
}: ItineraryDetailsLocationTimeProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isDifferentAirportNotificationEnabled = p('Result.DifferentAirportNotification.Enabled');
  const durationInDays =
    date.durationInDays(new Date(departureDate), new Date(arrivalDate)) ?? null;
  const { time, day } = useDateFormat();
  const { currentSearchData } = useSearch();
  const { language, timeFormat } = useSiteContext();
  const brandedFareInfoAndMarketingCarrier = { brandedFareInformation, marketingCarrier };
  const brandedFareName = useBrandedFareName(brandedFareInfoAndMarketingCarrier);

  const isDifferentAirport = (searchRouteKind: SearchRouteCodeKindType) => {
    if (window.location.pathname.includes(routes.CHANGE_TRIP)) {
      return false;
    }

    if (boundNumber === undefined) {
      return false;
    }

    return (
      isDifferentAirportNotificationEnabled &&
      !isMatchingLocation(currentSearchData?.routes?.[boundNumber][searchRouteKind] ?? '', {
        code: searchRouteKind === SearchRouteCodeKind.ORIGIN_CODE ? departureCode : arrivalCode,
        cityCode:
          searchRouteKind === SearchRouteCodeKind.ORIGIN_CODE
            ? departureCityCode ?? ''
            : arrivalCityCode ?? '',
      })
    );
  };

  const isTechnicalStopsEnabled = p('IbeClient.TripDetails.TechnicalStops.Enabled');
  const shouldShowTechnicalStops = isTechnicalStopsEnabled && numberOfTechnicalStops > 0;

  return (
    <div className={itineraryInfoStyles} data-testid="itinerary-details-location-time-container">
      <Stack spacing={4}>
        <Inline alignY="center" className={carrierDateStyles}>
          <Inline alignY="center" spacing={8}>
            {date.format(departureDate, day)}
            {Boolean(cabinClass) && <div className={dotSeparatorStyles} />}
            <span className={cabinClassStyles}>
              {brandedFareName || t(`IbeClient.CabinClass.${cabinClass}.Text`)}
            </span>
          </Inline>
          <div>
            {carriers.map((carrier) => (
              <CarrierLogo
                data-testid="itinerary-details-carrier-logos"
                id={carrier.code ?? ''}
                imageAlt={carrier.name as string}
                key={carrier.code}
              />
            ))}
          </div>
        </Inline>
        <Inline className={departureArrivalStyles}>
          <Inline spacing={4}>
            <span className={itineraryTimeStyles}>{date.format(departureDate, time)} - </span>
            <span className={itineraryTimeStyles}>{date.format(arrivalDate, time)}</span>
            {durationInDays > 0 && (
              <div
                className={overnightIndicatorStyles}
                data-testid="trip-bound-info-overnight"
              >{`+${durationInDays}`}</div>
            )}
          </Inline>
          {stops > 0 && <Stops stops={stops} />}
          {!stops && (
            <Text variant={textVariants._14_BOLD}>{t('TripDetails.Details.DirectFlight')}</Text>
          )}
        </Inline>

        <Inline
          alignY="center"
          className={cx(departureArrivalStyles, departureArrivalCitiesStyles)}
        >
          <Inline spacing={4}>
            <Text
              className={
                isDifferentAirport(SearchRouteCodeKind.ORIGIN_CODE) ? nearbyAirportStyles : null
              }
              data-testid={`itinerary-drawer-origin-${boundNumber}-airport-code`}
            >
              {departureCode}
            </Text>
            <Text>-</Text>
            <Text
              className={
                isDifferentAirport(SearchRouteCodeKind.DESTINATION_CODE)
                  ? nearbyAirportStyles
                  : null
              }
              data-testid={`itinerary-drawer-destination-${boundNumber}-airport-code`}
            >
              {arrivalCode}
            </Text>
          </Inline>
          <Label
            className="duration"
            data-testid="searchResults-segment-duration"
          >{`${LRE}${durationUtil.getDuration(
            duration,
            language.code,
            timeFormat,
            ['h', 'm'],
            true,
          )}${PDF}`}</Label>
        </Inline>
      </Stack>
      {(isSelfTransfer || stopoverType || trainSegment || shouldShowTechnicalStops) && (
        <Inline alignY="center" noWrap>
          <Inline className={badgesWrapper} spacing={4}>
            {isSelfTransfer && (
              <Inline
                alignY="center"
                className={selfTransferStyles}
                data-testid="itinerary-details-self-transfer"
              >
                <Inline alignY="center" spacing={4}>
                  <Icon color="#803D00" id="self-transfer" size="1rem" />
                  <span>{t('TripDetails.Details.SelfTransfer')}</span>
                </Inline>
              </Inline>
            )}
            {stopoverType && <StopoverInfoIndicator stopoverType={stopoverType} />}
          </Inline>
          <Stack align="end" spacing={4}>
            {shouldShowTechnicalStops && (
              <Text as="p" data-testid="itinerary-details-technical-stops">
                {numberOfTechnicalStops === 1
                  ? t('TripDetails.Details.TechnicalStop.Label.Singular', numberOfTechnicalStops)
                  : t('TripDetails.Details.TechnicalStop.Label.Plural', numberOfTechnicalStops)}
              </Text>
            )}
            {trainSegment === SOME && <p>{t('IbeClient.Segment.PartByTrain.Text')}</p>}
            {trainSegment === ALL && <p>{t('IbeClient.Segment.Train.Text')}</p>}
          </Stack>
        </Inline>
      )}
    </div>
  );
};

export default ItineraryDetailsLocationTime;
